import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { useEffect } from 'react';

/**
 * UI State Store
 * 
 * Manages global UI state including theme preferences and sidebar state.
 * Implements Requirements 4.1, 4.2, 4.3:
 * - Zustand for global state management
 * - TypeScript-typed store with full type inference
 * - Middleware for persistence and devtools
 */

export type Theme = 'light' | 'dark' | 'system';

export interface UIState {
  // State
  theme: Theme;
  sidebarCollapsed: boolean;
  
  // Actions
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

/**
 * UI Store with persistence and devtools middleware
 * 
 * Persists theme and sidebar preferences to localStorage
 * Integrates with Redux DevTools for state inspection
 */
export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        theme: 'system',
        sidebarCollapsed: false,

        // Actions
        setTheme: (theme) => 
          set({ theme }, false, 'setTheme'),

        toggleSidebar: () =>
          set(
            (state) => ({ sidebarCollapsed: !state.sidebarCollapsed }),
            false,
            'toggleSidebar'
          ),

        setSidebarCollapsed: (collapsed) =>
          set({ sidebarCollapsed: collapsed }, false, 'setSidebarCollapsed'),
      }),
      {
        name: 'ui-storage', // localStorage key
        partialize: (state) => ({
          theme: state.theme,
          sidebarCollapsed: state.sidebarCollapsed,
        }),
      }
    ),
    {
      name: 'UIStore', // DevTools name
    }
  )
);

/**
 * Selector hooks for optimized re-renders
 * Only subscribes to specific state slices
 */
export const useTheme = () => useUIStore((state) => state.theme);
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed);
export const useSetTheme = () => useUIStore((state) => state.setTheme);
export const useToggleSidebar = () => useUIStore((state) => state.toggleSidebar);

/**
 * Hook that syncs the theme to the DOM by toggling the `dark` class on <html>.
 * Handles "system" by listening to the OS media query.
 * Call this once at the app root (e.g. __root.tsx).
 */
export function useThemeEffect() {
  const theme = useTheme();

  useEffect(() => {
    const root = document.documentElement;

    function apply(dark: boolean) {
      if (dark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }

    if (theme === 'dark') {
      apply(true);
      return;
    }
    if (theme === 'light') {
      apply(false);
      return;
    }

    // "system" — follow OS preference
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    apply(mq.matches);

    const handler = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);
}
